import { SavedReport } from './ReportTabs.props';
import { DEFAULT_TAB_ID, closeableCodes } from './reportTabsHelpers';
import { TabMenuItem } from './TabContextMenu.props';

interface CloseActions {
  closeOthers: () => void;
  closeLeft: () => void;
  closeRight: () => void;
}

/** The three "close all except / left / right" rows, greyed when nothing is
 *  closeable in that direction (the default tab is never counted). */
const closeGroup = (tabIds: string[], targetId: string, a: CloseActions): TabMenuItem[] => [
  {
    key: 'close-others',
    label: 'Close all tabs except this',
    onClick: a.closeOthers,
    disabled: closeableCodes(tabIds, targetId, 'others').length === 0,
  },
  {
    key: 'close-left',
    label: 'Close tabs to left',
    onClick: a.closeLeft,
    disabled: closeableCodes(tabIds, targetId, 'left').length === 0,
  },
  {
    key: 'close-right',
    label: 'Close tabs to right',
    onClick: a.closeRight,
    disabled: closeableCodes(tabIds, targetId, 'right').length === 0,
  },
];

export interface ReportTabMenuActions extends CloseActions {
  closeThis: () => void;
  /** Omitted for read-only consumers that can't set defaults. */
  setAsDefault?: () => void;
}

/** Context menu for a pinned report tab. */
export const buildReportTabMenu = (
  tabIds: string[],
  code: string,
  a: ReportTabMenuActions
): TabMenuItem[] => {
  const items: TabMenuItem[] = [
    { key: 'close', label: 'Close this tab', onClick: a.closeThis },
    ...closeGroup(tabIds, code, a),
  ];
  if (a.setAsDefault) {
    items.push({
      key: 'set-default',
      label: 'Set tab as default',
      onClick: a.setAsDefault,
      dividerBefore: true,
    });
  }
  return items;
};

export interface DefaultTabMenuActions extends CloseActions {
  revertToDefaultReport?: () => void;
  revertToSystemDefaults?: () => void;
  save?: () => void;
}

/** Context menu for the default tab: the same close group (its own "close this"
 *  permanently disabled) plus the revert/save block. */
export const buildDefaultTabMenu = (
  tabIds: string[],
  hasDefaultReport: boolean,
  a: DefaultTabMenuActions
): TabMenuItem[] => {
  const items: TabMenuItem[] = [
    { key: 'close', label: 'Close this tab', disabled: true, hint: "Can't close — default tab" },
    ...closeGroup(tabIds, DEFAULT_TAB_ID, a),
  ];

  // The first item of the revert/save block carries the divider, wherever that
  // block starts (some rows are conditional).
  let dividerPending = true;
  const withDivider = (item: TabMenuItem): TabMenuItem => {
    if (!dividerPending) return item;
    dividerPending = false;
    return { ...item, dividerBefore: true };
  };

  if (hasDefaultReport && a.revertToDefaultReport) {
    items.push(
      withDivider({
        key: 'revert-default-report',
        label: 'Revert to default report',
        onClick: a.revertToDefaultReport,
      })
    );
  }
  if (a.revertToSystemDefaults) {
    items.push(
      withDivider({
        key: 'revert-system',
        label: 'Revert to system defaults',
        onClick: a.revertToSystemDefaults,
      })
    );
  }
  if (a.save) {
    items.push(
      withDivider({
        key: 'save',
        label: hasDefaultReport ? 'Save report as…' : 'Save as report…',
        onClick: a.save,
      })
    );
  }
  return items;
};

export interface TabContextInput {
  /** The right-clicked tab: DEFAULT_TAB_ID or a report code. */
  tabId: string;
  tabIds: string[];
  reports: SavedReport[];
  hasDefaultReport: boolean;
  /** False for read-only consumers that can't set a default report. */
  canSetDefault: boolean;
  /** Remove the given report codes (and revert to default if the active one closed). */
  closeCodes: (codes: string[]) => void;
  /** Open the set-as-default confirm flow for a report. */
  requestSetDefault: (report: SavedReport) => void;
  onRevertToDefaultReport?: () => void;
  onRevertToSystemDefaults?: () => void;
  onSave?: () => void;
}

/** Resolve the correct menu (default tab vs report tab) with its close-action
 *  closures bound. Keeps the assembly out of the component/hook. */
export const buildTabContextItems = (i: TabContextInput): TabMenuItem[] => {
  if (i.tabId === DEFAULT_TAB_ID) {
    return buildDefaultTabMenu(i.tabIds, i.hasDefaultReport, {
      closeOthers: () => i.closeCodes(closeableCodes(i.tabIds, DEFAULT_TAB_ID, 'others')),
      closeLeft: () => i.closeCodes(closeableCodes(i.tabIds, DEFAULT_TAB_ID, 'left')),
      closeRight: () => i.closeCodes(closeableCodes(i.tabIds, DEFAULT_TAB_ID, 'right')),
      revertToDefaultReport: i.onRevertToDefaultReport,
      revertToSystemDefaults: i.onRevertToSystemDefaults,
      save: i.onSave,
    });
  }
  const code = i.tabId;
  const report = i.reports.find((r) => r.code === code);
  return buildReportTabMenu(i.tabIds, code, {
    closeThis: () => i.closeCodes([code]),
    closeOthers: () => i.closeCodes(closeableCodes(i.tabIds, code, 'others')),
    closeLeft: () => i.closeCodes(closeableCodes(i.tabIds, code, 'left')),
    closeRight: () => i.closeCodes(closeableCodes(i.tabIds, code, 'right')),
    setAsDefault: i.canSetDefault && report ? () => i.requestSetDefault(report) : undefined,
  });
};
